import { Component, OnInit, TemplateRef, inject, viewChild } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { IgniteSubscriptionStatusService } from '@clubhub/services/ignite-subscription-status.service';
import { HeaderComponent as LibHeaderComponent } from '@shared/components/header/header.component';
import { SvgIconComponent } from '@shared/components/svg-icon/svg-icon.component';
import { UserFeedbackComponent } from '@shared/components/user-feedback';
import { roles } from '@shared/constants';
import { TWO_FACTOR_VERIFICATION_SERVICE } from '@shared/models';
import { ClientApps } from '@shared/models/enums';
import { AuthorizeService, ClubHubDataService, ConfigService, LenderHubDataService, ModalService } from '@shared/services';
import { AnalyticsService } from '@shared/services/analytics.service';
import { TwoFactorVerificationService } from '@shared/services/two-factor-verification.service';

@Component({
  selector: 'app-header',
  templateUrl: 'header.component.html',
  styleUrls: ['header.component.scss'],
  imports: [
    LibHeaderComponent,
    RouterLink,
    RouterLinkActive,
    UserFeedbackComponent,
    SvgIconComponent,
  ],
  providers: [
    TwoFactorVerificationService,
    LenderHubDataService,
    ClubHubDataService,
    {
      provide: TWO_FACTOR_VERIFICATION_SERVICE,
      useClass: TwoFactorVerificationService,
    },
  ],
})
export class HeaderComponent implements OnInit {
  public readonly authService = inject(AuthorizeService);
  private readonly configService = inject(ConfigService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly modalService = inject(ModalService);
  private readonly router = inject(Router);
  private readonly igniteSubscriptionStatusService = inject(IgniteSubscriptionStatusService);

  public readonly userFeedbackTemplate = viewChild.required<TemplateRef<unknown>>('userFeedbackTemplate');
  public roles = roles;
  public showNav: boolean = false;
  public clientApp: ClientApps = ClientApps.ClubHub;

  public ngOnInit() {
    this.authService.getUser().subscribe(user => {
      this.showNav = !this.authService.historyView || !!user;
      this.igniteSubscriptionStatusService.refreshSubscriptionStatus();
    });
  }

  public hasRole(role: string) {
    return this.authService.hasRole(role);
  }

  public ignite() {
    // Refresh subscription status each time user clicks on ignite to ensure we have the most up to date status
    this.igniteSubscriptionStatusService.refreshSubscriptionStatus();
    this.analyticsService.trackClick('Ignite');
  }

  public get hasAccessToMi() {
    return [
      roles.brokerMi,
      roles.brokerIntegrationMi,
      roles.executiveMarketSummaryMI,
      roles.brokerPaidMI,
      roles.brokerIgniteMI,
      roles.brokerNewKeyPartnerMI,
    ].some(r => this.hasRole(r));
  }

  public get showIgniteNav(): boolean {
    return this.authService.hasRole(roles.ignite);
  }

  public get showMortgageClubNavigation(): boolean {
    return this.authService.hasRole(roles.mortgageClub) && this.authService.agencyNumber !== null;
  }

  public get showResourceCentreLink(): boolean {
    return this.configService.applicationName === ClientApps.Ignite;
  }

  public get firstMiLink() {
    if (this.hasRole(roles.brokerMi)) {
      return '/mi';
    } else if (this.hasRole(roles.brokerIntegrationMi)) {
      return '/mi/integration';
    } else if (this.hasRole(roles.brokerNewKeyPartnerMI)) {
      return '/mi/key-partner';
    } else if (this.hasRole(roles.executiveMarketSummaryMI)) {
      return '/mi/executive-market-summary';
    } else if (this.hasRole(roles.brokerIgniteMI)) {
      return '/mi/broker-ignite';
    }
    return null;
  }

  public get isOnIgniteSection(): boolean {
    return this.router.url.startsWith('/ignite');
  }

  public get isOnMortgageClubSection(): boolean {
    return !this.router.url.startsWith('/ignite');
  }

  public get subscriptionStatus() {
    return this.igniteSubscriptionStatusService.subscriptionStatus$.value;
  }

  public get hasActiveSubscription(): boolean {
    return this.subscriptionStatus.active;
  }

  public get hasSubscriptionBroker(): boolean {
    const { active, broker } = this.subscriptionStatus;
    return active && broker;
  }

  public get hasSubscriptionAdmin(): boolean {
    const { active, accountOwner, accessManager } = this.subscriptionStatus;
    return active && (accountOwner || accessManager);
  }

  public get showTrialWarningBanner() {
    if (
      !this.subscriptionStatus.accountOwner || // Only show to account owners
      !this.subscriptionStatus.trialEndDate || // Don't show if there is no trial end date (i.e. non-Chargebee subscription, or has payment)
      !this.isOnIgniteSection // Only shown when on Ignite pages
    ) {
      return false;
    }

    const trialEndDate = new Date(this.subscriptionStatus.trialEndDate);
    return !Number.isNaN(trialEndDate.getTime()) && trialEndDate.getTime() > Date.now();
  }

  public get trialEndDuration() {
    if (!this.subscriptionStatus.trialEndDate) {
      return '';
    }

    const trialEndDate = new Date(this.subscriptionStatus.trialEndDate);
    if (Number.isNaN(trialEndDate.getTime())) {
      return '';
    }

    const now = new Date();
    const millisecondsUntilTrialEnd = trialEndDate.getTime() - now.getTime();
    const oneHourInMilliseconds = 60 * 60 * 1000;
    const oneDayInMilliseconds = 24 * oneHourInMilliseconds;

    if (millisecondsUntilTrialEnd < oneHourInMilliseconds) {
      return 'less than one hour';
    }

    if (millisecondsUntilTrialEnd < oneDayInMilliseconds) {
      const hours = Math.floor(millisecondsUntilTrialEnd / oneHourInMilliseconds);
      return `in ${hours} hour${hours === 1 ? '' : 's'}`;
    }

    const days = Math.floor(millisecondsUntilTrialEnd / oneDayInMilliseconds);
    return `in ${days} day${days === 1 ? '' : 's'}`;
  }

  public showFeedbackModal() {
    this.modalService.open({
      title: 'Provide Feedback',
      component: this.userFeedbackTemplate(),
    });
  }
}
